import { CLAIM } from '@constants/index'
import { ClaimRecord, ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { DateUtil } from './dateUtil'

export type AutoNullifyHandlerType = 'ready' | 'nullifyFailed' | 'timeExceeded' | 'none'

export type AutoNullifyHandlerCallback = (claimRecord?: ClaimRecord) => void

type HandleResultParams = {
  startTime: Date
  claimRecord: ClaimRecord
  handlers: Record<AutoNullifyHandlerType, AutoNullifyHandlerCallback>
  maxWaitTime?: number
}

interface AutoNullifyUtilReturn {
  isResultReady: (claimRecord: ClaimRecord, startTime: Date, maxWaitTime: number) => boolean
  isReadyToClaimOrQuote: (claimRecord: ClaimRecord) => boolean
  handleResult: (params: HandleResultParams) => void
}

const autoNullifyUtil = (): AutoNullifyUtilReturn => {
  const isReadyToClaimOrQuote = (claimRecord: ClaimRecord): boolean => {
    return (
      claimRecord.status === ClaimRecordStatus.READY_TO_CLAIM || claimRecord.status === ClaimRecordStatus.QUOTE_REQUIRED
    )
  }

  const isForManualNullify = (claimRecord: ClaimRecord): boolean => {
    return claimRecord.status === ClaimRecordStatus.FOR_MANUAL_NULLIFY
  }

  const isResultReady = (
    claimRecord: ClaimRecord,
    startTime: Date,
    maxWaitTime = CLAIM.MAX_NULLIFY_WAIT_TIME
  ): boolean => {
    if (!claimRecord) return false

    return (
      isReadyToClaimOrQuote(claimRecord) ||
      isForManualNullify(claimRecord) ||
      DateUtil.isMaxTimeExceeded({ startTime, maxTime: maxWaitTime })
    )
  }

  const getResultHandlerType = (
    claimRecord: ClaimRecord,
    startTime: Date,
    maxWaitTime: number
  ): AutoNullifyHandlerType => {
    if (isReadyToClaimOrQuote(claimRecord)) {
      return 'ready'
    } else if (isForManualNullify(claimRecord)) {
      return 'nullifyFailed'
    } else if (DateUtil.isMaxTimeExceeded({ startTime, maxTime: maxWaitTime })) {
      return 'timeExceeded'
    }
    return 'none'
  }

  const handleResult = (params: HandleResultParams) => {
    const { startTime, claimRecord, handlers, maxWaitTime = CLAIM.MAX_NULLIFY_WAIT_TIME } = params
    const resultType = getResultHandlerType(claimRecord, startTime, maxWaitTime)
    handlers[resultType]?.(claimRecord)
  }

  return {
    isResultReady,
    isReadyToClaimOrQuote,
    handleResult,
  }
}

export const AutoNullifyUtil = autoNullifyUtil()
